import { BookingResponseModel } from '../types/BookingModels';

const isValidIso = (value: string): boolean => {
  if (!value || value.trim() === '') return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

export const getTopicKey = (booking: BookingResponseModel): string => {
  const topicFromList = booking.sessionTopics?.[0];
  if (topicFromList?.id != null) return topicFromList.id.toString();

  return 'none';
};

export const getBookingGroupSignature = (booking: BookingResponseModel): string =>
  [
    booking.startIso,
    booking.endIso,
    booking.professionalId,
    booking.userId,
    booking.accepted,
    booking.rejected,
    booking.isCancelled,
  ].join('|');

const getCalendarDayKey = (isoString: string): string => {
  const date = new Date(isoString);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

/**
 * Groups bookings that form a single continuous session. Consecutive bookings
 * are combined when previous.endIso === next.startIso, they share the same
 * calendar day (local time), same professional/user, same status fields,
 * same paymentStatus, and same topic.
 */
export const groupBookingsForDecision = (
  bookings: BookingResponseModel[]
): BookingResponseModel[][] => {
  const valid = bookings.filter(
    (b) => isValidIso(b.startIso) && isValidIso(b.endIso)
  );

  const sorted = [...valid].sort((a, b) => {
    const startDiff = new Date(a.startIso).getTime() - new Date(b.startIso).getTime();
    if (startDiff !== 0) return startDiff;
    const endDiff = new Date(a.endIso).getTime() - new Date(b.endIso).getTime();
    if (endDiff !== 0) return endDiff;
    return a.id - b.id;
  });

  const groups: BookingResponseModel[][] = [];

  for (const booking of sorted) {
    const lastGroup = groups[groups.length - 1];
    const prev = lastGroup?.[lastGroup.length - 1];

    const canAppend =
      prev != null &&
      prev.endIso === booking.startIso &&
      getCalendarDayKey(prev.startIso) === getCalendarDayKey(booking.startIso) &&
      prev.professionalId === booking.professionalId &&
      prev.userId === booking.userId &&
      prev.accepted === booking.accepted &&
      prev.rejected === booking.rejected &&
      prev.isCancelled === booking.isCancelled &&
      prev.paymentStatus === booking.paymentStatus &&
      getTopicKey(prev) === getTopicKey(booking);

    if (canAppend) {
      lastGroup.push(booking);
    } else {
      groups.push([booking]);
    }
  }

  return groups.sort(
    (a, b) =>
      new Date(a[0].startIso).getTime() - new Date(b[0].startIso).getTime()
  );
};
